import { useCallback, useEffect, useRef, useState } from 'react';
import { PaymentPollingOptions } from './types';

export function usePaymentPolling<T>() {
	const [isPolling, setIsPolling] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const abortRef = useRef(false);
	const sessionIdRef = useRef(0);

	useEffect(() => {
		return () => {
			abortRef.current = true;

			if (timeoutRef.current !== undefined) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const stopPolling = useCallback(() => {
		abortRef.current = true;
		sessionIdRef.current += 1;

		if (timeoutRef.current !== undefined) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}

		setIsPolling(false);
	}, []);

	const startPolling = useCallback((options: PaymentPollingOptions<T>) => {
		if (timeoutRef.current !== undefined) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}

		abortRef.current = false;
		const sessionId = ++sessionIdRef.current;

		const {
			fetcher,
			shouldStop,
			onSuccess,
			onTimeout,
			onError,
			initialInterval = 500,
			backoffFactor = 1.5,
			maxAttempts = 10
		} = options;

		setIsPolling(true);

		let attempt = 0;
		let intervalMs = initialInterval;

		const isActive = () => !abortRef.current && sessionId === sessionIdRef.current;

		const runAttempt = async () => {
			if (!isActive()) {
				return;
			}

			attempt += 1;

			if (attempt > maxAttempts) {
				if (isActive()) {
					setIsPolling(false);
					onTimeout?.();
				}

				return;
			}

			try {
				const data = await fetcher();

				if (!isActive()) {
					return;
				}

				if (data !== null && shouldStop(data)) {
					onSuccess(data);
					setIsPolling(false);

					return;
				}

				if (attempt >= maxAttempts) {
					setIsPolling(false);
					onTimeout?.();

					return;
				}

				intervalMs *= backoffFactor;
				timeoutRef.current = setTimeout(() => {
					runAttempt();
				}, intervalMs);
			} catch (error) {
				if (!isActive()) {
					return;
				}

				console.warn(error);
				setIsPolling(false);
				onError?.(error);
			}
		};

		timeoutRef.current = setTimeout(() => {
			runAttempt();
		}, initialInterval);
	}, []);

	return { isPolling, startPolling, stopPolling };
}
